import React, {
  memo, useEffect, useRef, useState,
} from 'react'

import ReactTooltip from 'react-tooltip'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  bool, func, node, number, string,
} from 'prop-types'

import useBreakpoint from 'Utils/useBreakpoint'

// Renders react-tooltip directly instead of Triangle UI's Tooltip wrapper. The
// wrapper called a global ReactTooltip.hide() on every trigger's mouseleave, which
// cancelled the pending show timer of any neighbouring tooltip - so adjacent icons
// flickered or never appeared. react-tooltip's own per-instance mouseleave handles
// hiding, so leaving one trigger no longer touches its neighbours.
//
// Tooltips are hover-only affordances with no touch equivalent, so below the `sm`
// breakpoint (< 640px) we drop the tooltip and render the trigger alone. useBreakpoint
// reports the real width on the first client render, so mobile never mounts the
// tooltip; the wrapper has no layout footprint, so this causes no CLS. Controls that
// must open on tap use Triangle UI's Tooltip with `event="click"` instead.
//
// The `cgt-tooltip` class (and modifiers) are preserved so the existing ThreeDStudio
// SCSS, which targets `.cgt-tooltip.__react_component_tooltip`, keeps applying.
function DesktopOnlyTooltip({
  children,
  content = null,
  id,
  place = '',
  containerClass = '',
  className = '',
  tooltipClassName = '',
  clickable = false,
  delayShow = 0,
  delayHide = null,
  focusable = false,
  ariaLabel = '',
  overridePosition = undefined,
}) {
  const isMobile = useBreakpoint() === 'xs'
  const [hasHydrated, setHasHydrated] = useState(false)
  const triggerRef = useRef(null)

  useEffect(() => {
    setHasHydrated(true)
  }, [])

  // Defer the mobile bare-child return past hydration. SSR runs on the fast
  // ThreeDStudio page with no window, so useBreakpoint is undefined and the server
  // always emits the desktop wrapper. useBreakpoint reads the real width on the
  // first client render, so switching to the bare child there would change the
  // element tree mid-hydration (wrapper span vs bare button). Match the SSR
  // wrapper on the first render, then drop the tooltip on the next paint.
  if (hasHydrated && isMobile) {
    return children
  }

  // With no content there is no tooltip to show, but consumers still rely on the
  // wrapper for stable layout: ButtonSelector renders every button through this so a
  // button's flex box does not change type (bare <button> vs wrapped <span>) when it
  // gains a disabled-state tooltip, which reflowed the button-group row. Render the
  // same container/trigger spans without react-tooltip so wrapped and unwrapped
  // buttons occupy identical space on desktop.
  if (!content) {
    return (
      <span className={containerClass}>
        <span className={className}>{children}</span>
      </span>
    )
  }

  const tooltipClass = classnames('cgt-tooltip', tooltipClassName, clickable && 'cgt-tooltip--clickable')

  // Mirror Triangle's `tooltip-${id}` DOM id so markup stays byte-identical to the
  // previous implementation (no hydration mismatch, stable ids for any consumers).
  const tooltipId = `tooltip-${id}`

  // Keyboard path for hover-only tooltips around non-focusable content (e.g.
  // disabled buttons): the trigger itself becomes focusable and the card shows
  // on focus. Hiding is deferred to focus leaving the whole container so Tab
  // can reach interactive content inside the tooltip (its DOM node is the
  // trigger's sibling); Escape dismisses without moving focus.
  const focusTriggerProps = focusable
    ? {
      tabIndex: 0, ref: triggerRef, 'aria-label': ariaLabel || undefined, 'aria-describedby': tooltipId,
    }
    : {}
  const focusContainerProps = focusable
    ? {
      onFocus: () => ReactTooltip.show(triggerRef.current),
      onBlur: (event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) ReactTooltip.hide(triggerRef.current)
      },
      onKeyDown: (event) => {
        if (event.key === 'Escape') ReactTooltip.hide(triggerRef.current)
      },
    }
    : {}

  // Clickable tooltips default to a 500ms grace period so the pointer can
  // travel from the trigger into the card. An explicit delayHide (including
  // 0) always wins; `??` keeps 0 distinct from "unset".
  const delayHideValue = delayHide ?? (clickable ? 500 : 0)

  return (
    <span className={containerClass} {...focusContainerProps}>
      <span className={className} data-tip data-for={tooltipId} {...focusTriggerProps}>
        {children}
      </span>
      <ReactTooltip
        id={tooltipId}
        place={place}
        effect="solid"
        className={tooltipClass}
        delayShow={delayShow}
        delayHide={delayHideValue}
        globalEventOff="click"
        overridePosition={overridePosition}
      >
        {content}
      </ReactTooltip>
    </span>
  )
}

DesktopOnlyTooltip.propTypes = {
  children: node.isRequired,
  content: node,
  id: string.isRequired,
  place: string,
  containerClass: string,
  className: string,
  tooltipClassName: string,
  clickable: bool,
  delayShow: number,
  delayHide: number,
  focusable: bool,
  ariaLabel: string,
  overridePosition: func,
}

export default memo(DesktopOnlyTooltip)
